import { ModeComment } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { estiloBotonTablaJugador, IconButtonApp, InteractiveCardApp, TypographyApp } from '../custom/CustomComponents';
import ModalObservacionesJugador from '../jugador/ModalObservacionesJugador';

const BotonObservacionesLista = ({
  idJugador,
  observaciones = [],
  onClick,
  onUpdate,
  badge = false,
  compact = false,
  badgeText = null,
  showBadgeIcon = true,
  previewText = null,
  sx = {},
  iconSx = {},
}) => {
  const [open, setOpen] = useState(false);

  const tieneObservaciones = observaciones && observaciones.length > 0;

  const handleClick = (e) => {
    e.currentTarget?.blur();
    if (onClick) {
      onClick(e);
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleUpdate = (updatedObservaciones) => {
    if (onUpdate) {
      onUpdate(idJugador, updatedObservaciones);
    }
  };

  const textoNotas =
    badgeText ||
    (tieneObservaciones ? `${observaciones.length} ${observaciones.length === 1 ? 'nota' : 'notas'}` : 'Nota');

  return (
    <>
      {badge ? (
        <Tooltip
          title={
            badgeText
              ? 'Ver observación'
              : tieneObservaciones
                ? `Ver ${observaciones.length} ${observaciones.length === 1 ? 'observación' : 'observaciones'}`
                : 'Añadir observación'
          }
          arrow
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e);
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: compact ? 0 : '4px',
              height: 22,
              width: compact ? 26 : 'auto',
              px: compact ? 0 : 0.9,
              borderRadius: '5px',
              fontSize: '0.72rem',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease-in-out',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
              backgroundColor: tieneObservaciones ? '#e0f2fe' : '#f1f5f9',
              color: tieneObservaciones ? '#0369a1' : '#1e293b',
              border: tieneObservaciones ? '1px solid #7dd3fc' : '1px solid #cbd5e1',
              '&:hover': {
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.12)',
                filter: 'brightness(0.97)',
                transform: 'translateY(-0.5px)',
                backgroundColor: tieneObservaciones ? '#bae6fd' : '#e2e8f0',
                borderColor: tieneObservaciones ? '#38bdf8' : '#94a3b8',
                color: tieneObservaciones ? '#075985' : '#0f172a',
              },
              ...sx,
            }}
          >
            {showBadgeIcon && (
              <ModeComment
                sx={{
                  fontSize: '0.72rem',
                  color: tieneObservaciones ? '#0284c7' : '#475569',
                  flexShrink: 0,
                  ...iconSx,
                }}
              />
            )}
            {!compact && <span style={{ whiteSpace: 'nowrap' }}>{textoNotas}</span>}
          </Box>
        </Tooltip>
      ) : previewText ? (
        <InteractiveCardApp onClick={handleClick} sx={sx}>
          <TypographyApp
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            {previewText}
          </TypographyApp>
        </InteractiveCardApp>
      ) : (
        <Tooltip title="Ver observación" arrow>
          <IconButtonApp
            onClick={handleClick}
            enLista={true}
            border={!sx?.border}
            style={sx?.backgroundColor ? {} : estiloBotonTablaJugador(tieneObservaciones)}
            sx={{
              ...sx,
            }}
          >
            <ModeComment sx={{ fontSize: '0.85rem', ...iconSx }} />
          </IconButtonApp>
        </Tooltip>
      )}

      {!onClick && (
        <ModalObservacionesJugador
          open={open}
          onClose={handleClose}
          idJugador={idJugador}
          observaciones={observaciones}
          onUpdate={handleUpdate}
        />
      )}
    </>
  );
};

BotonObservacionesLista.propTypes = {
  idJugador: PropTypes.number.isRequired,
  observaciones: PropTypes.array,
  enLista: PropTypes.bool,
  badge: PropTypes.bool,
  compact: PropTypes.bool,
  badgeText: PropTypes.string,
  showBadgeIcon: PropTypes.bool,
  onClick: PropTypes.func, // Optional: if provided, overrides internal dialog
  onUpdate: PropTypes.func, // Optional: callback when observations are added/edited/deleted
  showAddButton: PropTypes.bool,
  previewText: PropTypes.string, // Optional: if provided, renders a text preview instead of an icon button
  sx: PropTypes.object,
  iconSx: PropTypes.object,
};

export default BotonObservacionesLista;
